import React, { createContext, useContext, useState, useEffect } from 'react';
import nocodeClient from '../lib/nocode';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Data mapping functions
const mapNcbCoachToApp = (ncbCoach) => ({
  id: ncbCoach.id,
  name: ncbCoach.name,
  username: ncbCoach.username,
  email: ncbCoach.email,
  role: ncbCoach.role,
  is_archived: ncbCoach.is_archived,
});

const mapAppCoachToNcb = (appCoach) => ({
  name: appCoach.name,
  username: appCoach.username,
  email: appCoach.email,
  password: appCoach.password,
  role: appCoach.role,
  is_archived: appCoach.is_archived,
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coaches, setCoaches] = useState([]);
  const [showArchived, setShowArchived] = useState(false);

  // Load coaches on initial load
  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        const response = await nocodeClient.read('coaches_bpa_5e7gf');
        const formattedCoaches = response.data.map(mapNcbCoachToApp);
        setCoaches(formattedCoaches || []);
      } catch (err) {
        console.error('Error fetching coaches:', err);
        // Fallback to mock data
        setCoaches([
          { id: 1, username: 'coach1', name: 'Sarah Johnson', role: 'Amazon FBA/FBM Specialist', email: 'sarah@example.com', is_archived: false },
          { id: 2, username: 'coach2', name: 'Mike Rodriguez', role: 'Mindset/Accountability Coach', email: 'mike@example.com', is_archived: false },
        ]);
      }
    };
    fetchCoaches();
  }, []);

  // Check for saved user session on initial load
  useEffect(() => {
    const savedUser = localStorage.getItem('marketing_mentor_user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    // ADMIN OVERRIDE - REMOVE AFTER SETUP
    if (username === 'admin' && password === 'admin123') {
      const adminUser = {
        id: 0,
        username: 'admin',
        name: 'System Administrator',
        role: 'Administrator',
        email: 'admin@example.com',
        is_admin: true,
      };
      setUser(adminUser);
      localStorage.setItem('marketing_mentor_user', JSON.stringify(adminUser));
      return true;
    }

    // PETER OVERRIDE
    if (username.toLowerCase() === 'peter' && password === 'Peanlo140569#$') {
      const peterUser = {
        id: 999,
        username: 'peter',
        name: 'Peter',
        role: 'Administrator',
        email: 'peter@example.com',
        is_admin: true,
      };
      setUser(peterUser);
      localStorage.setItem('marketing_mentor_user', JSON.stringify(peterUser));
      return true;
    }

    try {
      const response = await nocodeClient.search('coaches_bpa_5e7gf', { username: username, is_archived: false });
      const coach = response.data && response.data.length > 0 ? response.data[0] : null;

      if (coach && coach.password === password) {
        const userData = mapNcbCoachToApp(coach);
        delete userData.password;
        setUser(userData);
        localStorage.setItem('marketing_mentor_user', JSON.stringify(userData));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error in login:', err);
      return mockLogin(username, password);
    }
  };

  const mockLogin = (username, password) => {
    const coach = coaches.find(
      c => c.username === username && password === 'password123' && !c.is_archived
    );
    if (coach) {
      const userData = { ...coach };
      setUser(userData);
      localStorage.setItem('marketing_mentor_user', JSON.stringify(userData));
      return true;
    }
    return false;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('marketing_mentor_user');
  };

  const createCoach = async (coachData) => {
    const ncbPayload = mapAppCoachToNcb({ ...coachData, is_archived: false });
    const response = await nocodeClient.create('coaches_bpa_5e7gf', ncbPayload);
    const newCoach = mapNcbCoachToApp({ ...ncbPayload, id: response.id });
    setCoaches(prev => [...prev, newCoach]);
    return newCoach;
  };

  const updateCoach = async (coachData) => {
    const { id, ...updateData } = coachData;
    if (updateData.password === '') {
      delete updateData.password;
    }
    const ncbPayload = mapAppCoachToNcb(updateData);
    await nocodeClient.update('coaches_bpa_5e7gf', id, ncbPayload);

    const updatedCoach = { ...coachData };
    setCoaches(prev => prev.map(coach => coach.id === id ? updatedCoach : coach));

    if (user && user.id === id) {
      const updatedUser = { ...user, ...updateData };
      setUser(updatedUser);
      localStorage.setItem('marketing_mentor_user', JSON.stringify(updatedUser));
    }
    return updatedCoach;
  };

  const archiveCoach = async (coachId) => {
    await nocodeClient.update('coaches_bpa_5e7gf', coachId, { is_archived: true });
    setCoaches(prev => prev.map(coach => coach.id === coachId ? { ...coach, is_archived: true } : coach));
    if (user && user.id === coachId) {
      logout();
    }
    return true;
  };

  const restoreCoach = async (coachId) => {
    await nocodeClient.update('coaches_bpa_5e7gf', coachId, { is_archived: false });
    setCoaches(prev => prev.map(coach => coach.id === coachId ? { ...coach, is_archived: false } : coach));
    return true;
  };

  const toggleShowArchived = () => {
    setShowArchived(prev => !prev);
  };

  const getFilteredCoaches = () => {
    return showArchived ? coaches : coaches.filter(coach => !coach.is_archived);
  };

  const value = {
    user,
    login,
    logout,
    loading,
    coaches: getFilteredCoaches(),
    allCoaches: coaches,
    showArchived,
    toggleShowArchived,
    createCoach,
    updateCoach,
    archiveCoach,
    restoreCoach,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};